import { translateUi } from "@/shared/lib/ui-translate";
import { z } from "zod";

// Схемы для организаций
export const CreateOrganizationRequestSchema = z.lazy(() =>
  z.object({
  name: z
    .string()
    .min(2, { error: () => translateUi("litOrganizationsSchemas.nazvanieOrganizatsiiDolzhnoSoderzhatMinimum2") })
    .max(100, { error: () => translateUi("litOrganizationsSchemas.nazvanieOrganizatsiiNeDolzhnoPrevyshat100") })
    .trim(),
  url: z
    .string()
    .min(3, { error: () => translateUi("litOrganizationsSchemas.urlDolzhenSoderzhatMinimum3Simvola") })
    .max(50, { error: () => translateUi("litOrganizationsSchemas.urlNeDolzhenPrevyshat50Simvolov") })
    .regex(
      /^[a-z0-9-]+$/,
      { error: () => translateUi("litOrganizationsSchemas.urlMozhetSoderzhatTolkoStrochnyeBukvy") },
    )
    .optional()
    .or(z.literal("")),
  description: z
    .string()
    .max(500, { error: () => translateUi("litOrganizationsSchemas.opisanieNeDolzhnoPrevyshat500Simvolov") })
    .optional()
    .or(z.literal("")),
  avatarUrl: z.string().optional().or(z.literal("")).or(z.null()),
  bannerUrl: z.string().optional().or(z.literal("")).or(z.null()),
}));

export const UpdateOrganizationRequestSchema = z.lazy(() =>
  z.object({
  name: z
    .string()
    .min(2, { error: () => translateUi("litOrganizationsSchemas.nazvanieOrganizatsiiDolzhnoSoderzhatMinimum2") })
    .max(100, { error: () => translateUi("litOrganizationsSchemas.nazvanieOrganizatsiiNeDolzhnoPrevyshat100") })
    .trim()
    .optional(),
  url: z
    .string()
    .min(3, { error: () => translateUi("litOrganizationsSchemas.urlDolzhenSoderzhatMinimum3Simvola") })
    .max(50, { error: () => translateUi("litOrganizationsSchemas.urlNeDolzhenPrevyshat50Simvolov") })
    .regex(
      /^[a-z0-9-]+$/,
      { error: () => translateUi("litOrganizationsSchemas.urlMozhetSoderzhatTolkoStrochnyeBukvy") },
    )
    .optional()
    .or(z.literal("")),
  description: z
    .string()
    .max(500, { error: () => translateUi("litOrganizationsSchemas.opisanieNeDolzhnoPrevyshat500Simvolov") })
    .optional()
    .or(z.literal("")),
  avatarUrl: z.string().optional().or(z.literal("")).or(z.null()),
  bannerUrl: z.string().optional().or(z.literal("")).or(z.null()),
  // Social links (org profile) — allow raw handles/codes too
  discordUrl: z.string().max(200).optional().or(z.literal("")).or(z.null()),
  websiteUrl: z.string().max(300).optional().or(z.literal("")).or(z.null()),
  youtubeUrl: z.string().max(300).optional().or(z.literal("")).or(z.null()),
  twitchUrl: z.string().max(300).optional().or(z.literal("")).or(z.null()),
  twitterUrl: z.string().max(300).optional().or(z.literal("")).or(z.null()),
  telegramUrl: z.string().max(300).optional().or(z.literal("")).or(z.null()),
  vkUrl: z.string().max(300).optional().or(z.literal("")).or(z.null()),
  discordServerId: z
    .union([
      z
        .string()
        .min(17, { error: () => translateUi("litOrganizationsSchemas.discordServerIdDolzhenSoderzhatMinimum") })
        .max(20, { error: () => translateUi("litOrganizationsSchemas.discordServerIdNeDolzhenPrevyshat") }),
      z.literal(""),
      z.undefined(),
    ])
    .optional(),
}));

export const OrganizationStreamerSchema = z.lazy(() =>
  z.object({
  id: z.string().uuid({ error: () => translateUi("litOrganizationsSchemas.nekorrektnyyIdStrimera") }),
  twitchChannel: z.string().min(1, { error: () => translateUi("litOrganizationsSchemas.twitchKanalObyazatelen") }),
  displayName: z.string().nullish(),
  userId: z.string().uuid().nullish(),
  isOnline: z.boolean().default(false),
  isVisible: z.boolean().default(true).optional(),
  displayOrder: z.number().int().default(0).optional(),
}));

export const AddStreamerRequestSchema = z.lazy(() =>
  z.object({
  twitchChannel: z.string().min(1, { error: () => translateUi("litOrganizationsSchemas.twitchKanalObyazatelen") }).optional(),
  displayName: z.string().optional(),
  userId: z.string().uuid().optional(),
}).refine((v) => Boolean(v.userId || v.twitchChannel), {
  error: () => translateUi("litOrganizationsSchemas.useridIliTwitchchannelObyazatelen"),
}));

export const OrganizationSchema = z.lazy(() =>
  z.object({
  id: z.string().uuid({ error: () => translateUi("litOrganizationsSchemas.nekorrektnyyIdOrganizatsii") }),
  name: z.string().min(1, { error: () => translateUi("litOrganizationsSchemas.nazvanieOrganizatsiiObyazatelno") }),
  isVerified: z.boolean().optional().default(false),
  url: z.string().nullish(),
  tag: z.string().nullish(),
  description: z.string().nullish(),
  avatarUrl: z.string().nullish(),
  bannerUrl: z.string().nullish(),
  ownerId: z.string().uuid({ error: () => translateUi("litOrganizationsSchemas.nekorrektnyyIdVladeltsa") }),
  discordServerId: z.string().nullish(),
  discordUrl: z.string().nullish(),
  twitterUrl: z.string().nullish(),
  twitchUrl: z.string().nullish(),
  youtubeUrl: z.string().nullish(),
  websiteUrl: z.string().nullish(),
  telegramUrl: z.string().nullish(),
  vkUrl: z.string().nullish(),
  createdAt: z.string(),
  memberCount: z
    .number()
    .int()
    .min(0, { error: () => translateUi("litOrganizationsSchemas.kolichestvoUchastnikovNeMozhetBytOtritsatelnym") }),
  subscriberCount: z
    .number()
    .int()
    .min(0, { error: () => translateUi("litOrganizationsSchemas.kolichestvoPodpischikovNeMozhetBytOtritsatelnym") })
    .optional(),
  tournamentCount: z
    .number()
    .int()
    .min(0, { error: () => translateUi("litOrganizationsSchemas.kolichestvoTurnirovNeMozhetBytOtritsatelnym") }),
  currentUserRole: z.string().nullish(),
  streamers: z.array(OrganizationStreamerSchema).optional(),
}));

export const OrganizationMemberSchema = z.lazy(() =>
  z.object({
  id: z.string().uuid({ error: () => translateUi("litOrganizationsSchemas.nekorrektnyyIdUchastnika") }).optional(),
  organizationId: z.string().uuid({ error: () => translateUi("litOrganizationsSchemas.nekorrektnyyIdOrganizatsii") }),
  userId: z.string().uuid({ error: () => translateUi("litOrganizationsSchemas.nekorrektnyyIdPolzovatelya") }),
  userName: z.string().min(1, { error: () => translateUi("litOrganizationsSchemas.imyaPolzovatelyaObyazatelno") }),
  userDisplayName: z.string().optional(),
  userEmail: z.string().email({ error: () => translateUi("litOrganizationsSchemas.nekorrektnyyEmail") }).or(z.literal("")).nullish(),
  userAvatarUrl: z
    .string()
    .url({ error: () => translateUi("litOrganizationsSchemas.nekorrektnyyUrlAvatara") })
    .or(z.literal(""))
    .nullish(),
  twitchLogin: z.string().nullish(),
  roleId: z.number().int().min(1, { error: () => translateUi("litOrganizationsSchemas.idRoliDolzhenBytPolozhitelnymChislom") }),
  roleKey: z.string().optional(),
  roleName: z.string().min(1, { error: () => translateUi("litOrganizationsSchemas.nazvanieRoliObyazatelno") }),
  joinedAt: z.string().optional(),
  isOwner: z.boolean().default(false),
  isBanned: z.boolean().optional().default(false),
  bannedAt: z.string().nullable().optional(),
  bannedByUserName: z.string().nullable().optional(),
  isStreamer: z.boolean().optional().default(false),
  hasPro: z.boolean().optional(),
  proNicknameStyle: z.number().int().min(0).max(3).optional(),
  proNicknameColorA: z.string().nullable().optional(),
  proNicknameColorB: z.string().nullable().optional(),
}));

export const CreateOrganizationMemberRequestSchema = z.lazy(() =>
  z.object({
  email: z.string().email({ error: () => translateUi("litOrganizationsSchemas.nekorrektnyyEmail") }),
  roleId: z.number().int().min(1, { error: () => translateUi("litOrganizationsSchemas.idRoliDolzhenBytPolozhitelnymChislom") }),
}));

export const BanMemberRequestSchema = z.lazy(() =>
  z.object({
  reason: z
    .string()
    .min(5, { error: () => translateUi("litOrganizationsSchemas.prichinaBanaDolzhnaSoderzhatMinimum5") })
    .max(200, { error: () => translateUi("litOrganizationsSchemas.prichinaBanaNeDolzhnaPrevyshat200") })
    .trim(),
}));

export const SubscriptionSchema = z.lazy(() =>
  z.object({
  organizationId: z.string().uuid({ error: () => translateUi("litOrganizationsSchemas.nekorrektnyyIdOrganizatsii") }),
  createdAt: z.string().datetime({ error: () => translateUi("litOrganizationsSchemas.nekorrektnayaDataPodpiski") }),
}));

// Типы для экспорта
export type OrganizationStreamerDTO = z.infer<
  typeof OrganizationStreamerSchema
>;
